import { Bars3Icon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { useNavigate } from '@tanstack/react-router';
import { useUIStore } from '@/store/uiStore';
import { useChatStore } from '@/store/chatStore';
import { WakeWordIndicator } from './WakeWordController';
import { NotificationCenter } from '@/components/shared/NotificationCenter';

interface TopBarProps {
  title?: string;
}

export function TopBar({ title = 'New chat' }: TopBarProps) {
  const openNavDrawer = useUIStore((s) => s.openNavDrawer);
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const navigate = useNavigate();

  // Start a fresh chat and always land on the chat screen. Reuse the current
  // session if it is already empty so we don't pile up blank "New chat" rows.
  const handleNewChat = async () => {
    const { messages, createNewSession } = useChatStore.getState();
    if (messages.length > 0) await createNewSession();
    navigate({ to: '/' });
  };

  return (
    <header
      className="sticky top-0 z-30 flex h-14 items-center justify-between gap-2 border-b border-outline-variant/20 bg-bg-surface/80 px-2 backdrop-blur-md safe-area-inset-top safe-x"
      role="banner"
    >
      {/* Mobile: open the overlay drawer */}
      <button
        onClick={() => openNavDrawer()}
        aria-label="Open navigation"
        className="md:hidden rounded-lg p-2.5 text-text-secondary transition-colors hover:bg-surface-container-high hover:text-text-primary"
      >
        <Bars3Icon className="h-5 w-5" />
      </button>

      {/* Desktop: collapse/expand (slide) the sidebar rail */}
      <button
        onClick={toggleSidebar}
        aria-label="Toggle sidebar"
        className="hidden md:inline-flex rounded-lg p-2.5 text-text-secondary transition-colors hover:bg-surface-container-high hover:text-text-primary"
      >
        <Bars3Icon className="h-5 w-5" />
      </button>

      <h1 className="truncate text-sm font-semibold text-text-primary">{title}</h1>

      <div className="flex items-center gap-1.5">
        <WakeWordIndicator />
        <NotificationCenter />
        <button
          onClick={handleNewChat}
          aria-label="New chat"
          className="rounded-lg p-2.5 text-text-secondary transition-colors hover:bg-surface-container-high hover:text-text-primary"
        >
          <PencilSquareIcon className="h-5 w-5" />
        </button>
      </div>
    </header>
  );
}
